import PlanDetails from "@/components/plans/FloatingPlanDetails";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useAgencySeats, useCampaignsLeft, useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {INFINITY_THRESHOLD} from "@/store/constants";
import {
  isFloatingBuyoutMenuVisibleAtom,
  isMobileSidebarOpenAtom,
  isMoreBuyoutsModalOpenAtom,
  isSidebarProfileMenuOpenAtom,
} from "@/store/jotaiAtoms";
import type {TFloatingBuyouts} from "@/store/types";
import {useLocation} from "@tanstack/react-router";
import {useAtomValue} from "jotai";
import {type FC, useEffect} from "react";
import type {PlacesType} from "react-tooltip";
import {useToggle} from "react-use";

const FloatingBuyoutMenu: FC<{
  className?: string;
  place?: PlacesType;
  type?: TFloatingBuyouts;
}> = ({className, place, type = "buyouts"}) => {
  const {pathname} = useLocation();
  const seats = useAgencySeats();
  const campaignsLeft = useCampaignsLeft();
  const {credits, isCreditSystemActive, remainingBuyouts} = usePackageLimits();
  const isAdmin = useIsAdmin();
  const isModalOpen = useAtomValue(isMoreBuyoutsModalOpenAtom);
  const isProfileMenuOpen = useAtomValue(isSidebarProfileMenuOpenAtom);
  const isMobileSidebarOpen = useAtomValue(isMobileSidebarOpenAtom);
  const isVisibleAtom = useAtomValue(isFloatingBuyoutMenuVisibleAtom);
  const getIsVisible = () => {
    const regex =
      /^(\/agency\/\d+\/dashboard\/?|\/agency\/\d+\/subbrand\/\d+\/dashboard\/?|\/company\/\d+\/dashboard\/?)/;

    if (
      (type === "buyouts" && remainingBuyouts < INFINITY_THRESHOLD) ||
      (type === "campaigns" && campaignsLeft! < INFINITY_THRESHOLD) ||
      (type === "seats" && seats < INFINITY_THRESHOLD)
    ) {
      return regex.test(pathname) || isVisibleAtom;
    }

    if (
      type === "credits" &&
      (isCreditSystemActive || !!credits) &&
      (isAdmin || !!credits) &&
      (pathname.includes("/campaign/") || pathname.includes("/dashboard") || pathname.includes("/invoices"))
    ) {
      return true;
    }

    return false;
  };

  const [animationTriggerToggle, toggleAnimationTrigger] = useToggle(false);

  useEffect(() => {
    const shouldAnimateOut = isModalOpen.show || isProfileMenuOpen || isMobileSidebarOpen;
    toggleAnimationTrigger(!shouldAnimateOut);
  }, [isMobileSidebarOpen, isModalOpen.show, isProfileMenuOpen, toggleAnimationTrigger]);

  if (!getIsVisible()) {
    return null;
  }

  return (
    <div
      className={clsxm(
        "pointer-events-auto flex origin-bottom-left flex-col rounded-md bg-white transition-all duration-500 ease-in-out",
        !animationTriggerToggle && "pointer-events-none -translate-x-4 translate-y-8 scale-0",
        className,
      )}>
      <PlanDetails place={place} type={type} />
    </div>
  );
};

export default FloatingBuyoutMenu;
